import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { flexRender } from '@tanstack/react-table'
import {
  Table as BaseTable, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../Table'
import EmptyState from '../EmptyState'
import Loader from '../../shared/Loader'
import FloatingHeader from './FloatingHeader'
import useDataTableLayout from './useDataTableLayout'
import {
  DATA_TABLE_HEAD_CLASS, DATA_TABLE_TABLE_CLASS, DATA_TABLE_ACTIONS_COL_CLASS,
  DATA_TABLE_ACTIONS_STICKY_CLASS
} from './styles'

// Two cell-rendering paths: columns[].cell uses TanStack flexRender (default);
// RenderRow(row) is an escape hatch for custom <tr> wiring (refs, popovers, inline editors).

const WRAPPER_CLASS = 'tw:overflow-x-auto tw:[scrollbar-width:none] '
  + 'tw:[&::-webkit-scrollbar]:hidden'
// Jira pattern: the border lives on this non-scrolling outer frame, so the inner scroll element
// Stays borderless and the sticky column reaches the true scroll end. overflow-clip (NOT hidden)
// Clips the rounded corners WITHOUT becoming a scroll container, so the inner fake scrollbar keeps
// Sticking to the viewport (hidden would pin it to the frame) and shares the frame's inner width.
const OUTER_FRAME_CLASS = 'tw:relative tw:border tw:border-solid tw:border-gray-300 '
  + 'tw:rounded! tw:overflow-clip!'
const FLOATING_SCROLLBAR_CLASS = 'tw:sticky tw:bottom-0 tw:z-20 tw:overflow-x-auto '
  + 'tw:overflow-y-hidden tw:h-4 tw:bg-white tw:border-t tw:border-solid tw:border-gray-200'
// Position:fixed twin of the bottom scrollbar, pinned to the viewport while the
// Table overflows and its bottom edge is still below the fold (the in-flow bar is
// Clipped by the frame and hides there). Width/left track the wrapper, as FloatingHeader does.
const FIXED_SCROLLBAR_CLASS = 'tw:fixed tw:bottom-0 tw:z-30 tw:overflow-x-auto '
  + 'tw:overflow-y-hidden tw:h-4 tw:bg-white tw:border-t tw:border-solid tw:border-gray-200'
const LOADER_OVERLAY_CLASS = 'tw:absolute tw:inset-x-0 tw:bottom-0 tw:top-12 '
  + 'tw:bg-white/70 tw:z-10'
const LOADER_STICKY_CLASS = 'tw:sticky tw:top-20 tw:flex tw:justify-center tw:py-4'
// The placeholder cell spans the full (possibly overflowing) table width; pinning its
// Content sticky-left at the wrapper's visible width keeps the empty/error state
// Centered in the viewport instead of drifting off when the table scrolls horizontally.
const PLACEHOLDER_STICKY_CLASS = 'tw:sticky tw:left-0'
// The whole actions column is sticky right (pinned during horizontal scroll). The header
// Keeps an opaque gray-50 bg + ds-table-sticky-h separator while not at the scroll end
// (Jira-style). Body cells stay transparent while scrolling (content shows through) and
// Take the row bg only at rest — see TriggeredAlertsRow.
const ACTIONS_HEADER_CLASS = `${DATA_TABLE_ACTIONS_COL_CLASS} ${DATA_TABLE_ACTIONS_STICKY_CLASS} `
  + 'tw:bg-gray-50! tw:hover:bg-gray-200! tw:text-center!'

const DEFAULT_EMPTY = <EmptyState message="" title="Aucune donnée" />
const DEFAULT_ERROR = (
  <EmptyState
    icon="exclamation-triangle"
    message="Une erreur est survenue lors du chargement. Veuillez réessayer plus tard."
    title="Chargement impossible"
  />
)

const renderHeaderContent = header => {
  if (header.isPlaceholder)
    return null
  return flexRender(header.column.columnDef.header, header.getContext())
}

const renderHeaderCell = header =>
  <TableHeader key={header.id}>{renderHeaderContent(header)}</TableHeader>

const renderHeaderCells = headerGroup => headerGroup.headers.map(renderHeaderCell)

const renderActionsHeader = (actionsHeader, showSeparator) => {
  if (!actionsHeader)
    return null
  const className = showSeparator ? `${ACTIONS_HEADER_CLASS} tw:ds-table-sticky-h!` : ACTIONS_HEADER_CLASS
  return <TableHeader className={className}>{actionsHeader}</TableHeader>
}

const renderDefaultRow = row => (
  <TableRow key={row.id}>
    {row.getVisibleCells().map(cell => (
      <TableCell key={cell.id}>
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    ))}
  </TableRow>
)

const DataTable = ({
  table,
  loading = false,
  error = null,
  pagination = null,
  actionsHeader = null,
  renderRow = null,
  emptyState = DEFAULT_EMPTY,
  errorState = DEFAULT_ERROR
}) => {
  const { rows } = table.getRowModel()
  const headerGroups = table.getHeaderGroups()
  const allColumns = table.getAllColumns()
  const columnsSignature = allColumns.map(col => col.id).join(',')
  const layout = useDataTableLayout(rows.length, columnsSignature)
  const hasError = !loading && Boolean(error)
  const isEmpty = !loading && !hasError && rows.length === 0
  const showPlaceholder = hasError || isEmpty
  const colSpan = useMemo(() => allColumns.length + (actionsHeader ? 1 : 0), [
    allColumns.length, actionsHeader
  ])

  const headerRow = (
    <TableRow>
      {renderHeaderCells(headerGroups[0])}
      {renderActionsHeader(actionsHeader, !layout.atScrollEnd)}
    </TableRow>
  )

  return (
    <>
      <div className={OUTER_FRAME_CLASS}>
        <div
          ref={layout.wrapperRef}
          className={WRAPPER_CLASS}
          onScroll={layout.handleScroll}
        >
          <BaseTable
            className={DATA_TABLE_TABLE_CLASS}
            hover={!showPlaceholder}
            responsive={false}
            bordered
          >
            <TableHead className={DATA_TABLE_HEAD_CLASS}>{headerRow}</TableHead>
            <TableBody>
              {showPlaceholder ? (
                <TableRow>
                  <TableCell className="tw:p-0!" colSpan={colSpan}>
                    <div
                      className={PLACEHOLDER_STICKY_CLASS}
                      style={{ width: layout.wrapperRect.width || '100%' }}
                    >
                      {hasError ? errorState : emptyState}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
              {!showPlaceholder && rows.map(row =>
                renderRow ? renderRow(row, layout.atScrollEnd) : renderDefaultRow(row))}
            </TableBody>
          </BaseTable>
          {loading ? (
            <div className={LOADER_OVERLAY_CLASS}>
              <div className={LOADER_STICKY_CLASS}><Loader /></div>
            </div>
          ) : null}
        </div>
        {layout.tableScrollWidth > layout.wrapperRect.width ? (
          <div
            ref={layout.floatingBarRef}
            aria-hidden="true"
            className={FLOATING_SCROLLBAR_CLASS}
            onScroll={layout.handleScroll}
          >
            <div style={{ width: layout.tableScrollWidth, height: '1px' }} />
          </div>
        ) : null}
      </div>
      <FloatingHeader
        colWidths={layout.colWidths}
        scrollRef={layout.cloneScrollRef}
        scrollWidth={layout.tableScrollWidth}
        visible={layout.showCloneHeader}
        wrapperRect={layout.wrapperRect}
        onScroll={layout.handleScroll}
      >
        {headerRow}
      </FloatingHeader>
      {layout.showFloatingBar ? (
        <div
          ref={layout.floatingBarCloneRef}
          aria-hidden="true"
          className={FIXED_SCROLLBAR_CLASS}
          style={{ left: layout.wrapperRect.left, width: layout.wrapperRect.width }}
          onScroll={layout.handleScroll}
        >
          <div style={{ width: layout.tableScrollWidth, height: '1px' }} />
        </div>
      ) : null}
      {showPlaceholder ? null : pagination}
    </>
  )
}

DataTable.propTypes = {
  actionsHeader: PropTypes.node,
  emptyState: PropTypes.node,
  error: PropTypes.instanceOf(Error),
  errorState: PropTypes.node,
  loading: PropTypes.bool,
  pagination: PropTypes.node,
  renderRow: PropTypes.func,
  table: PropTypes.shape({
    getAllColumns: PropTypes.func.isRequired,
    getHeaderGroups: PropTypes.func.isRequired,
    getRowModel: PropTypes.func.isRequired
  }).isRequired
}

DataTable.defaultProps = {
  actionsHeader: null,
  emptyState: DEFAULT_EMPTY,
  error: null,
  errorState: DEFAULT_ERROR,
  loading: false,
  pagination: null,
  renderRow: null
}

export default DataTable
